import { Box, useTheme } from '@mui/material';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface MvpLevelSelectorProps {
    onLevelSelect: (level: number) => void;
    disabled?: boolean;
}

export default function MvpLevelSelector({ onLevelSelect, disabled = false }: MvpLevelSelectorProps) {
    const theme = useTheme();
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const [selectedLevel, setSelectedLevel] = useState(0);

    const getLevelColor = (levelIndex: number) => {
        return theme.palette.levels?.[levelIndex as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    const handleLevelClick = (level: number) => {
        if (disabled) return;
        setSelectedLevel(level);
        onLevelSelect(level);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {Array.from({ length: levelDefinitions.length }, (_, i) => i + 1).map((level) => (
                <Box
                    key={level}
                    onClick={() => handleLevelClick(level)}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor:
                            selectedLevel === level
                                ? getLevelColor(level)
                                : theme.palette.mode === 'dark'
                                  ? theme.palette.grey[700]
                                  : theme.palette.grey[300],
                        cursor: disabled ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: disabled ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: selectedLevel === level ? 'white' : theme.palette.text.primary,
                        fontWeight: 600,
                        '&:hover': disabled
                            ? {}
                            : {
                                  transform: 'scale(1.05)',
                                  backgroundColor: getLevelColor(level),
                              },
                    }}
                    title={`${levelDefinitions[level - 1]?.title || `Level ${level}`}: ${levelDefinitions[level - 1]?.description || ''}`}
                >
                    {level}
                </Box>
            ))}
        </Box>
    );
}
